import { useState, useEffect } from "react";
import Card from "../components/Card";
import { apiClient } from "../api/client";
import { useAuth } from "../context/AuthContext";

interface DashboardStats {
  totalEvaluations: number;
  pendingEvaluations: number;
  completedEvaluations: number;
  totalTeams: number;
}

function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats>({
    totalEvaluations: 0,
    pendingEvaluations: 0,
    completedEvaluations: 0,
    totalTeams: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);

        // Add a small delay to ensure auth state is fully set after login
        await new Promise(resolve => setTimeout(resolve, 100));

        // ดึงข้อมูล evaluations
        const evaluations = await apiClient.get<any>("/evaluations");
        const evaluationList = evaluations.evaluations || [];

        // ดึงข้อมูล teams
        const teams = await apiClient.get<any>("/teams");
        const teamsList = teams.teams || [];

        const pending = evaluationList.filter(
          (e: any) => e.status === "DRAFT" || e.status === "SUBMITTED"
        ).length;
        const completed = evaluationList.filter((e: any) => e.status === "COMPLETED").length;

        setStats({
          totalEvaluations: evaluationList.length,
          pendingEvaluations: pending,
          completedEvaluations: completed,
          totalTeams: teamsList.length,
        });
        setError(null);
      } catch (err: any) {
        console.error("Failed to fetch dashboard stats:", err);
        setError(err.message || "Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchStats();
    }
  }, [user]);

  const items = [
    { label: "Total Evaluations", value: stats.totalEvaluations, hint: "All evaluations" },
    { label: "Completed", value: stats.completedEvaluations, hint: "Ready for review" },
    { label: "Pending", value: stats.pendingEvaluations, hint: "Need action" },
    { label: "Teams", value: stats.totalTeams, hint: "Active teams" },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-500">Welcome back</p>
          <h2 className="text-2xl font-semibold text-slate-900">{user?.name || "Dashboard"}</h2>
        </div>
        <span className="px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-sm font-medium">
          {error ? "Check connection" : "All systems nominal"}
        </span>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <div className="grid md:grid-cols-4 gap-4">
        {items.map((item) => (
          <Card key={item.label} title={item.label} subtitle={item.hint}>
            <div className="text-3xl font-semibold text-slate-900">{item.value}</div>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default Dashboard;
